import { AppProps } from 'next/app'
import Head from 'next/head'
import React, { FC, useEffect, useState } from 'react'

// eslint-disable-next-line import/no-extraneous-dependencies
import { create } from 'jss'
import rtl from 'jss-rtl'
import TagManager from 'react-gtm-module'
import { ModalProvider } from 'react-modal-hook'
import { QueryClient, QueryClientProvider } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { ToastContainer } from 'react-toastify'
import { TransitionGroup } from 'react-transition-group'

import { Theme, ThemeProvider } from '@material-ui/core'
import CssBaseline from '@material-ui/core/CssBaseline'
import { StylesProvider, jssPreset } from '@material-ui/core/styles'

import ServiceWorkerWrapper from '~/components/layouts/ServiceWorkerWrapper'
import { Loader } from '~/components/shared'
import LocalizedProvider from '~/containers/LocalizedProvider'
import OfflineNotification from '~/views/OfflineNotification'

import useAppTheme from '~/hooks/useAppTheme'
import useNetwork from '~/hooks/useNetwork'
import REF from '~/services/REF'
import { isCenter, isClinic, updateUserToken } from '~/state/modules/user'
import { AppState, wrapper } from '~/state/store'
import { askPermission, subscribeUserToPush } from '~/utils/pushNotification'

import { STORAGE } from '~/constants/storage'

import { IS_BROWSER } from '~/constants'
import theme from '~/theme'

import 'react-toastify/dist/ReactToastify.css'

if (process.env.NODE_ENV !== 'development') {
  if (IS_BROWSER) {
    if (process.env.NEXT_GTM_ID) {
      const tagManagerArgs = {
        gtmId: process.env.NEXT_GTM_ID
      }

      TagManager.initialize(tagManagerArgs)
    }
  }
}

// Configure JSS
const jss = create({ plugins: [...jssPreset().plugins, rtl()] })

const App: FC<AppProps> = ({ Component, pageProps }) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)

  const { isOffline, onClose } = useNetwork()

  const center = useSelector(isCenter)
  const clinic = useSelector(isClinic)
  const { id, role, ui_setting } = useSelector((state: AppState) => state.user)
  const [queryClient] = React.useState(() => new QueryClient())

  useEffect(() => {
    async function fetchData(): Promise<void> {
      try {
        const jwt = localStorage.getItem(STORAGE.authToken)

        if (jwt) {
          await dispatch<any>(updateUserToken(jwt))
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return
    }

    if (!('PushManager' in window)) {
      return
    }

    const subscribe = async (): Promise<any> => {
      const subscribeUser = await subscribeUserToPush()

      if (id) {
        if (center) {
          REF.updateCenter(id, JSON.parse(JSON.stringify(subscribeUser)))
        }

        if (clinic) {
          REF.updateClinic(id, JSON.parse(JSON.stringify(subscribeUser)))
        }
      }
    }

    if (role) {
      askPermission()

      subscribe()
    }
  }, [role, id])

  const appTheme = useAppTheme(ui_setting)

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
        />
        <title>Ref Dental</title>
      </Head>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <StylesProvider jss={jss}>
        <ThemeProvider<Theme> theme={appTheme ?? theme}>
          <LocalizedProvider>
            <QueryClientProvider client={queryClient}>
              <ModalProvider rootComponent={TransitionGroup}>
                {loading ? (
                  <Loader height="100vh" noGutters />
                ) : (
                  <Component {...pageProps} />
                )}

                {isOffline && (
                  <OfflineNotification
                    isOffline={isOffline}
                    onClose={onClose}
                  />
                )}
                <CssBaseline />
              </ModalProvider>
            </QueryClientProvider>
            <ServiceWorkerWrapper />
          </LocalizedProvider>
        </ThemeProvider>
      </StylesProvider>
    </>
  )
}

export default wrapper.withRedux(App)
